/**
 * QuickSaleWizard - Venta Rápida para pantallas touch
 * Optimizado para emisión rápida de documentos DTE
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Divider,
  IconButton,
  Alert,
  Chip,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack,
  ArrowForward,
  Send,
  ReceiptLong,
  Person,
  ShoppingCart,
  Search,
  PointOfSale,
} from '@mui/icons-material';

import {
  UNIDADES_MEDIDA,
  TIPOS_VENTA,
  TIPOS_ITEM,
  METODOS_PAGO,
  FORMAS_PAGO,
  COUNTRIES,
} from './config/dteConfig';

import ReceptorSearch from './steps/ReceptorSearch';
import { authenticatedFetch, parseJsonResponse, settingsService } from '../../../services/api';
import { validateDtePayload } from './utils/schemaValidator';

const QUICK_SALE_DTE_TYPES = [
  { code: '01', name: 'Factura Electrónica', shortName: 'FE', color: '#1976d2' },
  { code: '03', name: 'Crédito Fiscal', shortName: 'CCF', color: '#388e3c' },
  { code: '14', name: 'Factura Sujeto Excluido', shortName: 'FSE', color: '#f57c00' },
];

const isEmptyish = (value) => value === '' || value === null || value === undefined;

// Quitar campos opcionales "vacíos" del JSON final (no enviar null ni "")
// Nota: esto NO es un "clean" genérico; aplica solo a paths opcionales que MH/AJV validan por tipo/length.
const stripEmptyOptionalFields = (dteJson) => {
  if (!dteJson || typeof dteJson !== 'object') return dteJson;

  const next = (typeof structuredClone === 'function')
    ? structuredClone(dteJson)
    : JSON.parse(JSON.stringify(dteJson));

  // identificacion (campos de contingencia opcionales)
  if (next.identificacion && typeof next.identificacion === 'object') {
    if (isEmptyish(next.identificacion.tipoContingencia)) next.identificacion.tipoContingencia = null;
    if (isEmptyish(next.identificacion.motivoContin)) next.identificacion.motivoContin = null;
  }

  // documentoRelacionado/otrosDocumentos/ventaTercero/extension/apendice (opcionales)
  if (isEmptyish(next.documentoRelacionado) || (Array.isArray(next.documentoRelacionado) && next.documentoRelacionado.length === 0)) next.documentoRelacionado = null;
  if (isEmptyish(next.otrosDocumentos) || (Array.isArray(next.otrosDocumentos) && next.otrosDocumentos.length === 0)) next.otrosDocumentos = null;
  if (isEmptyish(next.ventaTercero) || (typeof next.ventaTercero === 'object' && next.ventaTercero && Object.keys(next.ventaTercero).length === 0)) next.ventaTercero = null;
  if (isEmptyish(next.extension) || (typeof next.extension === 'object' && next.extension && Object.keys(next.extension).length === 0)) next.extension = null;
  if (isEmptyish(next.apendice) || (Array.isArray(next.apendice) && next.apendice.length === 0)) next.apendice = null;

  // receptor: normalizar por tipoDte para evitar additionalProperties en schemas
  if (next.receptor && typeof next.receptor === 'object') {
    const tipoDte = String(next.identificacion?.tipoDte || '');

    // Telefono vacío -> null (schemas suelen permitir null)
    if (isEmptyish(next.receptor.telefono)) next.receptor.telefono = null;

    if (tipoDte === '01' || tipoDte === '04' || tipoDte === '11') {
      // FE/NR/FEX: schema receptor NO tiene nombreComercial
      const {
        tipoDocumento,
        numDocumento,
        nrc,
        nombre,
        codActividad,
        descActividad,
        direccion,
        telefono,
        correo,
      } = next.receptor;
      next.receptor = {
        tipoDocumento,
        numDocumento,
        nrc,
        nombre,
        codActividad,
        descActividad,
        direccion,
        telefono,
        correo,
      };
    } else {
      // CCF/NC/ND/etc: nombreComercial existe y no puede ir ""
      if (isEmptyish(next.receptor.nombreComercial)) next.receptor.nombreComercial = next.receptor.nombre || 'N/A';
    }
  }

  // resumen.pagos: quitar campos opcionales vacíos
  if (next.resumen?.pagos && Array.isArray(next.resumen.pagos)) {
    next.resumen.pagos = next.resumen.pagos.map((p) => {
      if (!p || typeof p !== 'object') return p;
      const pago = { ...p };
      if (isEmptyish(pago.referencia)) pago.referencia = null;
      if (isEmptyish(pago.plazo)) pago.plazo = null;
      if (isEmptyish(pago.periodo)) pago.periodo = null;
      return pago;
    });
  }

  // cuerpoDocumento: quitar campos opcionales vacíos
  if (Array.isArray(next.cuerpoDocumento)) {
    next.cuerpoDocumento = next.cuerpoDocumento.map((item) => {
      if (!item || typeof item !== 'object') return item;
      const nextItem = { ...item };
      // En schemas MH varios de estos vienen como required pero permiten null (no se deben omitir)
      if (isEmptyish(nextItem.numeroDocumento)) nextItem.numeroDocumento = null;
      if (isEmptyish(nextItem.codTributo)) nextItem.codTributo = null;
      // codigo suele ser required string; si viene vacío, mantenerlo para que el validador lo marque
      return nextItem;
    });
  }

  return next;
};

// Función de truncamiento a X decimales (no redondeo)
const truncate = (num, decimals) => {
  const factor = Math.pow(10, decimals);
  return Math.floor(num * factor) / factor;
};

// Función round estándar a X decimales
const round = (num, decimals) => {
  const factor = Math.pow(10, decimals);
  return Math.round(num * factor) / factor;
};

const IVA_RATE = 0.13;
const IVA_FACTOR = 1 + IVA_RATE;

const toBaseAmount = (amount) => round((parseFloat(amount) || 0) / IVA_FACTOR, 2);
const toBaseUnitAmount = (amount) => round((parseFloat(amount) || 0) / IVA_FACTOR, 3);
const toIvaAmount = (baseAmount) => round((parseFloat(baseAmount) || 0) * IVA_RATE, 2);
const toGrossAmount = (baseAmount) => round((parseFloat(baseAmount) || 0) + toIvaAmount(baseAmount), 2);

const getNowInElSalvador = () => {
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/El_Salvador',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const byType = Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
  return {
    fecEmi: `${byType.year}-${byType.month}-${byType.day}`,
    horEmi: `${byType.hour}:${byType.minute}:${byType.second}`,
  };
};

const getEmisorData = () => {
  try {
    const modules = localStorage.getItem('nx2026_modules');
    if (modules) {
      const parsed = JSON.parse(modules);
      if (parsed && parsed.length > 0 && parsed[0].company) {
        const company = parsed[0].company;
        return {
          id: company.id,
          nit: company.nit || '',
          nrc: company.nrc || '',
          legal_name: company.name || company.legal_name || '',
          commercial_name: company.commercial_name || '',
          economic_activity_code: company.economic_activity_code || '',
          economic_activity_desc: company.economic_activity_desc || '',
          main_departamento_code: company.main_departamento_code || '',
          main_municipio_code: company.main_municipio_code || '',
          main_address: company.main_address || '',
          bill_phone: company.bill_phone || company.phone || '',
          bill_email: company.bill_email || company.email || '',
          environment: company.environment || 'test'
        };
      }
    }
  } catch (e) {
    console.error('Error getting emisor data:', e);
  }
  return null;
};

const generateNumeroControl = (tipoDte, codEstableMH, codPuntoVenta, correlativo = 1) => {
  const estableMH = (codEstableMH && codEstableMH.length >= 4) 
    ? codEstableMH.substring(0, 4).toUpperCase() 
    : 'M001';
  
  const puntoVenta = (codPuntoVenta && /^[A-Za-z]\d{3}$/.test(codPuntoVenta))
    ? codPuntoVenta.toUpperCase()
    : `P${String(codPuntoVenta || '001').padStart(3, '0')}`;
  
  const estableSinGuion = `${estableMH}${puntoVenta}`;
  const correlativoStr = String(correlativo).padStart(15, '0');
  
  return `DTE-${tipoDte}-${estableSinGuion}-${correlativoStr}`;
};

const normalizeBranchValue = (value) => String(value || '').trim();
const normalizePosValue = (value) => {
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return '';
  if (/^[A-Z]\d{3}$/.test(raw)) return raw;

  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  return `P${digits.padStart(3, '0').slice(-3)}`;
};

const resolveSelectedBranch = (branches, header) => {
  const selectedBranchId = normalizeBranchValue(header?.branchId);
  const selectedBranchCode = normalizeBranchValue(header?.sucursal);

  return branches.find((branch) => {
    const branchId = normalizeBranchValue(branch?.id);
    const branchCode = normalizeBranchValue(branch?.codigo_establecimiento);

    if (selectedBranchId && branchId === selectedBranchId) {
      return true;
    }

    return selectedBranchCode && branchCode === selectedBranchCode;
  }) || null;
};

const parseInvoiceJsonData = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const mergeBackendDteIdentity = (localJson, persistedJson) => {
  if (!persistedJson) return localJson;

  const nextJson = {
    ...localJson,
    identificacion: {
      ...(localJson?.identificacion || {}),
    },
    emisor: {
      ...(localJson?.emisor || {}),
    },
  };

  if (persistedJson?.identificacion?.numeroControl) {
    nextJson.identificacion.numeroControl = persistedJson.identificacion.numeroControl;
  }

  const persistedEmisor = persistedJson?.emisor || {};
  const localTipoDte = localJson?.identificacion?.tipoDte;

  if (localTipoDte === '07') {
    nextJson.emisor.codigoMH = persistedEmisor.codigoMH || nextJson.emisor.codigoMH;
    nextJson.emisor.codigo = persistedEmisor.codigo || nextJson.emisor.codigo;
    nextJson.emisor.puntoVentaMH = persistedEmisor.puntoVentaMH || nextJson.emisor.puntoVentaMH;
    nextJson.emisor.puntoVenta = persistedEmisor.puntoVenta || nextJson.emisor.puntoVenta;
  } else {
    nextJson.emisor.codEstableMH = persistedEmisor.codEstableMH || nextJson.emisor.codEstableMH;
    nextJson.emisor.codEstable = persistedEmisor.codEstable || nextJson.emisor.codEstable;
    nextJson.emisor.codPuntoVentaMH = persistedEmisor.codPuntoVentaMH || nextJson.emisor.codPuntoVentaMH;
    nextJson.emisor.codPuntoVenta = persistedEmisor.codPuntoVenta || nextJson.emisor.codPuntoVenta;
  }

  return nextJson;
};

const numberToWords = (num) => {
  if (num === null || num === undefined || isNaN(num)) return 'CERO DOLARES';
  
  const entero = Math.floor(num);
  const decimales = Math.round((num - entero) * 100);
  
  const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
  const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];
  
  const convertGroup = (n) => {
    if (n === 0) return '';
    if (n < 10) return unidades[n];
    if (n < 20) return especiales[n - 10];
    if (n < 100) {
      const dec = Math.floor(n / 10);
      const uni = n % 10;
      return decenas[dec] + (uni > 0 ? ' Y ' + unidades[uni] : '');
    }
    if (n < 1000) {
      const cent = Math.floor(n / 100);
      const rest = n % 100;
      if (n === 100) return 'CIEN';
      return centenas[cent] + (rest > 0 ? ' ' + convertGroup(rest) : '');
    }
    if (n < 1000000) {
      const mil = Math.floor(n / 1000);
      const rest = n % 1000;
      if (mil === 1) return 'MIL' + (rest > 0 ? ' ' + convertGroup(rest) : '');
      return convertGroup(mil) + ' MIL' + (rest > 0 ? ' ' + convertGroup(rest) : '');
    }
    if (n < 1000000000) {
      const millon = Math.floor(n / 1000000);
      const rest = n % 1000000;
      if (millon === 1) return 'UN MILLÓN' + (rest > 0 ? ' ' + convertGroup(rest) : '');
      return convertGroup(millon) + ' MILLONES' + (rest > 0 ? ' ' + convertGroup(rest) : '');
    }
    return n.toString();
  };
  
  const parteEntera = convertGroup(entero);
  
  if (decimales > 0) {
    return `${parteEntera} DOLARES CON ${decimales < 10 ? '0' : ''}${decimales} CENTAVOS`;
  } else {
    return `${parteEntera} DOLARES EXACTOS`;
  }
};

const buildCompleteDTEJson = (document, company, branches) => {
  const tipoDte = document.identificacion.tipoDte;
  // Usar la fecha de emisión del documento o la fecha actual por defecto
  const nowSv = getNowInElSalvador();
  const fecEmi = document.identificacion?.fecEmi || nowSv.fecEmi;
  const horEmi = nowSv.horEmi;
  
  const selectedBranch = resolveSelectedBranch(branches, document.header);
  
  const puntoVentaRaw = document.header?.puntoVenta || '001';
  const codPuntoVenta = puntoVentaRaw.match(/^[A-Za-z]\d{3}$/)
    ? puntoVentaRaw.toUpperCase()
    : `P${String(puntoVentaRaw).padStart(3, '0')}`;
  
  const codEstableMH = selectedBranch?.codigo_establecimiento
    ? selectedBranch.codigo_establecimiento.substring(0, 4).toUpperCase()
    : 'M001';
  
  const codEstable = document.header?.sucursal
    ? String(document.header.sucursal).padStart(4, '0').substring(0, 4)
    : '0001';
  
  const emisor = {
    nit: (company?.nit || '').replace(/-/g, ''),
    nrc: company?.nrc || '',
    nombre: company?.legal_name || company?.nombre || 'CONAMYPE',
    codActividad: company?.economic_activity_code || '',
    descActividad: company?.economic_activity_desc || '',
    nombreComercial: company?.commercial_name || '',
    direccion: {
      departamento: String(company?.main_departamento_code || '06').padStart(2, '0'),
      municipio: String(company?.main_municipio_code || '01').slice(-2).padStart(2, '0'),
      distrito: String(company?.main_distrito_code || company?.main_district_code || ''),
      complemento: company?.main_address || ''
    },
    telefono: company?.bill_phone || company?.phone || '',
    correo: company?.bill_email || company?.email || '',
    codEstable,
    codPuntoVenta
  };
  
  // Determinar la versión según el tipo de documento
  const getVersion = (tipo) => {
    const versionMap = {
      '01': 2,
      '03': 4,
      '04': 4,
      '05': 4,
      '06': 4,
      '07': 2,
      '08': 2,
      '09': 2,
      '11': 3,
      '14': 2,
      '15': 2,
    };
    return versionMap[tipo] || 1;
  };

  const version = getVersion(tipoDte);
  
  const identificacion = {
    version,
    ambiente: company?.environment === 'production' ? '01' : '00',
    tipoDte,
    numeroControl: document.identificacion?.numeroControl || generateNumeroControl(tipoDte, codEstableMH, codPuntoVenta, 1),
    codigoGeneracion: crypto.randomUUID().toUpperCase(),
    tipoModelo: 1,
    tipoOperacion: 1,
    tipoContingencia: null,
    motivoContin: null,
    fecEmi,
    horEmi,
    tipoMoneda: 'USD'
  };
  
  let receptor = null;
  let sujetoExcluido = null;
  
  if (tipoDte === '14' || tipoDte === '15') {
    sujetoExcluido = {
      tipoDocumento: document.receptor?.tipoDocumento || '13',
      numDocumento: document.receptor?.numDocumento || '',
      nombre: document.receptor?.nombre || '',
      codActividad: document.receptor?.codActividad || '',
      descActividad: document.receptor?.descActividad || '',
      // IMPORTANTE: municipio debe ser solo 2 dígitos (no 4 como viene del catálogo)
      direccion: document.receptor?.direccion?.departamento ? {
        departamento: String(document.receptor.direccion.departamento).slice(-2).padStart(2, '0'),
        municipio: document.receptor?.direccion?.municipio 
          ? String(document.receptor.direccion.municipio).slice(-2).padStart(2, '0') 
          : null,
        distrito: String(document.receptor?.direccion?.distrito || ''),
        complemento: document.receptor?.direccion?.complemento || ''
      } : null,
      telefono: document.receptor?.telefono || document.receptor?.whatsapp || '',
      correo: document.receptor?.correoNotificacion || null
    };
  } else if (tipoDte === '03' || tipoDte === '07' || tipoDte === '08' || tipoDte === '09') {
    // CCF, CR, CLE, DCLE - requieren NRC y nit
    // IMPORTANTE: El tipoDocumento puede venir como string o número, normalizar a string para comparar
    const tipoDocReceptor = String(document.receptor?.tipoDocumento || '');
    const esNIT = tipoDocReceptor === '36';
    
    // IMPORTANTE: municipio debe ser solo 2 dígitos (no 4 como viene del catálogo)
    const receptorMunicipio = document.receptor?.direccion?.municipio 
      ? String(document.receptor.direccion.municipio).slice(-2).padStart(2, '0') 
      : null;
    
    receptor = {
      nit: esNIT ? (document.receptor?.numDocumento || '').replace(/-/g, '') : '',  // Quitar guiones del NIT
      nrc: document.receptor?.nrc || '',
      nombre: document.receptor?.nombre || '',
      codActividad: document.receptor?.codActividad || '',
      descActividad: document.receptor?.descActividad || '',
      nombreComercial: document.receptor?.nombreComercial || null,
      direccion: document.receptor?.direccion?.departamento ? {
        departamento: String(document.receptor.direccion.departamento).slice(-2).padStart(2, '0'),
        municipio: receptorMunicipio,
        distrito: String(document.receptor?.direccion?.distrito || ''),
        complemento: document.receptor?.direccion?.complemento || ''
      } : null,
      telefono: document.receptor?.telefono || '',
      correo: document.receptor?.correoNotificacion || ''
    };
  } else {
    const tieneReceptorValido = document.receptor && 
      document.receptor.tipoDocumento && 
      document.receptor.numDocumento && 
      document.receptor.nombre;
    
    const tieneDireccion = tieneReceptorValido && 
      document.receptor.direccion && 
      document.receptor.direccion.departamento && 
      document.receptor.direccion.municipio;
    
    // IMPORTANTE: municipio debe ser solo 2 dígitos (no 4 como viene del catálogo)
    const receptorMunicipio = tieneDireccion && document.receptor.direccion.municipio
      ? String(document.receptor.direccion.municipio).slice(-2).padStart(2, '0')
      : null;
    
    const receptorTipoDocumento = tieneReceptorValido ? String(document.receptor.tipoDocumento) : '36';
    const rawNumDocumento = tieneReceptorValido ? String(document.receptor.numDocumento || '') : '00000000-0';
    const normalizedNumDocumento = receptorTipoDocumento === '36'
      ? rawNumDocumento.replace(/\D/g, '') // schema FE: ^([0-9]{14}|[0-9]{9})$
      : rawNumDocumento;

    const nrcDigits = tieneReceptorValido ? String(document.receptor.nrc || '').replace(/\D/g, '') : '';
    const telefonoValue = tieneReceptorValido ? (document.receptor.telefono || document.receptor.whatsapp || '') : '';
    const correoValue = tieneReceptorValido ? (document.receptor.correoNotificacion || document.receptor.correo || '') : '';

    receptor = {
      tipoDocumento: receptorTipoDocumento,
      numDocumento: normalizedNumDocumento,
      // En FE, nrc es requerido pero puede ser null (evitar "")
      nrc: nrcDigits ? nrcDigits : null,
      nombre: tieneReceptorValido ? document.receptor.nombre : 'CONSUMIDOR FINAL',
      codActividad: tieneReceptorValido && document.receptor.codActividad ? document.receptor.codActividad : null,
      descActividad: tieneReceptorValido && document.receptor.descActividad ? document.receptor.descActividad : null,
      direccion: tieneDireccion ? {
        departamento: String(document.receptor.direccion.departamento).slice(-2).padStart(2, '0'),
        municipio: receptorMunicipio,
        distrito: String(document.receptor.direccion.distrito || ''),
        complemento: document.receptor.direccion.complemento || ''
      } : null,
      telefono: telefonoValue ? telefonoValue : null,
      correo: correoValue ? correoValue : null
    };
  }
  
  const cuerpoDocumento = document.cuerpoDocumento.map((item, index) => {
    const cantidad = parseFloat(item.cantidad) || 1;
    
    if (tipoDte === '14' || tipoDte === '15') {
      const compraTotal = (parseFloat(item.precioUni) || 0) * cantidad - (parseFloat(item.montoDescu) || 0) * cantidad;
      return {
        numItem: index + 1,
        tipoItem: item.tipoItem || 1,
        cantidad,
        codigo: item.codigo || item.code || '',
        // uniMedida debe ser siempre INT
        uniMedida: parseInt(item.uniMedida, 10) || 13,
        descripcion: item.descripcion || '',
        precioUni: parseFloat((parseFloat(item.precioUni) || 0).toFixed(8)),
        montoDescu: parseFloat(item.montoDescu) || 0,
        compra: compraTotal,
        tributos: null,
        noGravado: 0
      };
    } else if (tipoDte === '03') {
      const precioConIva = parseFloat(item.precioConIva ?? item.precioUni) || 0;
      const montoDescuConIva = parseFloat(item.montoDescu) || 0;
      const totalConIva = round((precioConIva * cantidad) - (montoDescuConIva * cantidad), 2);
      const ventaGravada = toBaseAmount(totalConIva);
      const precioSinIva = cantidad > 0 ? round(ventaGravada / cantidad, 3) : toBaseUnitAmount(precioConIva);
      const montoDescu = toBaseUnitAmount(montoDescuConIva);
      
      return {
        numItem: index + 1,
        tipoItem: item.tipoItem || 1,
        numeroDocumento: null,
        cantidad,
        codigo: item.codigo || item.code || '',
        codTributo: null,
        // uniMedida debe ser siempre INT
        uniMedida: parseInt(item.uniMedida, 10) || 13,
        descripcion: item.descripcion || '',
        precioUni: precioSinIva,
        montoDescu,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ventaGravada,
        tributos: ["20"],  // Campo correcto según schema MH: array con código de tributo IVA 13%
        psv: 0,
        noGravado: 0
      };
    } else if (tipoDte === '08' || tipoDte === '09') {
      // precioUni viene como precio SIN IVA del cart
      const precioSinIva = Math.round(parseFloat(item.precioUni) * 100) / 100 || 0;
      const montoDescu = parseFloat(item.montoDescu) || 0;
      // Cálculo correcto según regla MH
      const bruto = parseFloat(((precioSinIva - montoDescu) * cantidad).toFixed(2));
      const ventaGravada = bruto;  // precio completo, IVA incluido
      const ivaItem = Math.floor(bruto * 13 / 113 * 100) / 100;
      
      return {
        numItem: index + 1,
        tipoItem: item.tipoItem || 1,
        numeroDocumento: null,
        cantidad,
        codigo: item.codigo || item.code || '',
        codTributo: null,
        // uniMedida debe ser siempre INT
        uniMedida: parseInt(item.uniMedida, 10) || 13,
        descripcion: item.descripcion || '',
        precioUni: precioSinIva,
        montoDescu,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ventaGravada,
        tributos: ["20"],  // Campo correcto según schema MH: array con código de tributo IVA 13%
        psv: 0,
        noGravado: 0,
        ivaItem
      };
    } else if (tipoDte === '01') {
      // FE v1 - Factura Electrónica: precio CON IVA viene del cart
      // Cálculo correcto según MH: extraer IVA directamente del total (× 13/113)
      const precioConIva = parseFloat(item.precioConIva) || 0;
      const montoDescu = parseFloat(item.montoDescu) || 0;
      const cantidad = parseFloat(item.cantidad) || 0;

      const totalConIva = precioConIva * cantidad;
      const totalDescu = montoDescu * cantidad;

      // ventaGravada = total con IVA menos descuento (precio ya incluye IVA)
      const ventaGravada = parseFloat((totalConIva - totalDescu).toFixed(2));

      // IVA se extrae directamente del total: * 13/113 (no pasar por totalSinIva redondeado)
      const ivaItem = parseFloat((ventaGravada * 13 / 113).toFixed(2));

      // precioUni CON IVA para que MH valide: cantidad * precioUni = ventaGravada
      const precioUni = parseFloat((ventaGravada / cantidad).toFixed(2));
      
      return {
        numItem: index + 1,
        tipoItem: item.tipoItem || 1,
        numeroDocumento: null,
        cantidad,
        codigo: item.codigo || item.code || '',
        codTributo: null,
        // uniMedida debe ser siempre INT
        uniMedida: parseInt(item.uniMedida, 10) || 13,
        descripcion: item.descripcion || '',
        precioUni,
        montoDescu,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ventaGravada,
        ivaItem,
        tributos: null,
        psv: 0,
        noGravado: 0
      };
    }
  });
  
  let resumen = {};
  
  console.log('[QUICKSALE] buildCompleteDTEJson - Generando resumen para tipoDte:', tipoDte);
  console.log('[QUICKSALE] cuerpoDocumento:', JSON.stringify(cuerpoDocumento, null, 2));
  
  if (tipoDte === '14' || tipoDte === '15') {
    const totalCompra = cuerpoDocumento.reduce((sum, item) => sum + (item.compra || 0), 0);
    const reteRenta = totalCompra * 0.10;
    const totalPagar = totalCompra - reteRenta;
    resumen = {
      totalCompra,
      descu: 0,
      totalDescu: 0,
      subTotal: totalCompra,
      ivaRete1: 0,
      reteRenta,
      totalPagar,
      totalLetras: numberToWords(totalPagar),
      condicionOperacion: document.header?.formaPago === 'credito' ? 2 : 1,
      pagos: [{ codigo: document.header?.metodoPago || '01', montoPago: totalPagar, referencia: null, plazo: null, periodo: null }],
      observaciones: 'Compra a persona natural no inscrita en IVA.'
    };
  } else if (tipoDte === '03' || tipoDte === '08' || tipoDte === '09') {
    const totalGravada = round(cuerpoDocumento.reduce((sum, item) => sum + (item.ventaGravada || 0), 0), 2);
    const totalDescu = tipoDte === '03'
      ? round(document.cuerpoDocumento.reduce((sum, item) => {
          const cantidad = parseFloat(item.cantidad) || 1;
          const descuentoConIva = parseFloat(item.montoDescu) || 0;
          return sum + toBaseAmount(descuentoConIva * cantidad);
        }, 0), 2)
      : round(cuerpoDocumento.reduce((sum, item) => sum + ((item.montoDescu || 0) * item.cantidad), 0), 2);
    const totalIva = tipoDte === '03'
      ? toIvaAmount(totalGravada)
      : round(cuerpoDocumento.reduce((sum, item) => sum + (item.ivaItem || 0), 0), 2);
    const subTotal = totalGravada;
    const montoTotalOperacion = tipoDte === '03'
      ? toGrossAmount(subTotal)
      : round(subTotal, 2);
    const totalPagar = montoTotalOperacion;
    
    resumen = {
      totalNoSuj: 0, totalExenta: 0, totalGravada, subTotalVentas: totalGravada,
      descuNoSuj: 0, descuExenta: 0, descuGravada: totalDescu, porcentajeDescuento: 0, totalDescu,
      tributos: [{ codigo: '20', descripcion: 'Impuesto al Valor Agregado 13%', valor: totalIva }],
      subTotal,  // Ahora es subTotalVentas - totalDescu = 2920
      ivaPerci1: 0,  // Campo requerido por schema
      ivaRete1: 0, reteRenta: 0, montoTotalOperacion,
      totalNoGravado: 0, totalPagar, totalLetras: numberToWords(totalPagar),
      saldoFavor: 0, condicionOperacion: document.header?.formaPago === 'credito' ? 2 : 1,
      pagos: [{ codigo: document.header?.metodoPago || '01', montoPago: totalPagar, referencia: null, plazo: null, periodo: null }],
      numPagoElectronico: null
    };
  } else {
    const totalGravada = Math.round(cuerpoDocumento.reduce((sum, item) => sum + (item.ventaGravada || 0), 0) * 100) / 100;
    // totalIva debe ser la suma de ivaItem para que coincida
    const totalIva = Math.round(cuerpoDocumento.reduce((sum, item) => sum + (item.ivaItem || 0), 0) * 100) / 100;
    // montoTotalOperacion = totalGravada (ya que ventaGravada incluye el IVA)
    const montoTotalOperacion = totalGravada;
    const totalPagar = montoTotalOperacion;
    const montoPagoRedondeado = Math.round(montoTotalOperacion * 100) / 100;
    
    // FE v1: NO incluye ivaItem en resumen - solo totalIva
    
    // FE v1: NO tiene ivaItem ni ivaPerci1 en resumen - solo totalIva
    resumen = {
      totalNoSuj: 0, totalExenta: 0, totalGravada, subTotalVentas: totalGravada,
      descuNoSuj: 0, descuExenta: 0, descuGravada: 0, porcentajeDescuento: 0, totalDescu: 0,
      // FE v1 solo usa totalIva para el IVA
      tributos: null, subTotal: montoTotalOperacion, ivaRete1: 0, reteRenta: 0,
      montoTotalOperacion, totalNoGravado: 0, totalPagar,
      totalLetras: numberToWords(totalPagar), totalIva,
      saldoFavor: 0, condicionOperacion: document.header?.formaPago === 'credito' ? 2 : 1,
      pagos: [{ codigo: document.header?.metodoPago || '01', montoPago: montoPagoRedondeado, referencia: null, plazo: null, periodo: null }],
      numPagoElectronico: null
    };
  }
  
  const jsonFinal = {
    identificacion,
    documentoRelacionado: null,
    emisor,
    receptor,
    otrosDocumentos: null,
    ventaTercero: null,
    cuerpoDocumento,
    resumen,
    extension: null,
    apendice: null
  };
  
  if (tipoDte === '14' || tipoDte === '15') {
    jsonFinal.sujetoExcluido = sujetoExcluido;
    delete jsonFinal.receptor;
  }
  
  return jsonFinal;
};

const QuickSaleWizard = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [document, setDocument] = useState({
    identificacion: { tipoDte: '', fecEmi: new Date().toISOString().split('T')[0] },
    header: { formaPago: 'contado', metodoPago: '01', sucursal: '0', branchId: null, puntoVenta: '', posId: null },
    receptor: null,
    cuerpoDocumento: [],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [schemaDiagnostics, setSchemaDiagnostics] = useState([]);
  const [whatsappCountry, setWhatsappCountry] = useState('503');
  const [clienteMode, setClienteMode] = useState('existente');
  const [fechaEmision, setFechaEmision] = useState(new Date().toISOString().split('T')[0]);
  
  const [emisorData, setEmisorData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  
  const [products, setProducts] = useState([]);
  const [searchProduct, setSearchProduct] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  
  const [cart, setCart] = useState([]);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTouch = useMediaQuery(theme.breakpoints.down('lg'));

  const getQuickSaleBranchStorageKey = (companyId) =>
    companyId ? `nx2026_quick_sale_branch_${companyId}` : 'nx2026_quick_sale_branch_default';

  const getQuickSalePosStorageKey = (companyId, branchCode) =>
    companyId && branchCode
      ? `nx2026_quick_sale_pos_${companyId}_${branchCode}`
      : 'nx2026_quick_sale_pos_default';

  const persistQuickSaleBranch = (companyId, codigoEstablecimiento) => {
    try {
      localStorage.setItem(getQuickSaleBranchStorageKey(companyId), String(codigoEstablecimiento || ''));
    } catch {
      // ignore
    }
  };

  const getPersistedQuickSaleBranch = (companyId) => {
    try {
      return localStorage.getItem(getQuickSaleBranchStorageKey(companyId)) || '';
    } catch {
      return '';
    }
  };

  const persistQuickSalePos = (companyId, branchCode, posId) => {
    try {
      localStorage.setItem(getQuickSalePosStorageKey(companyId, branchCode), String(posId || ''));
    } catch {
      // ignore
    }
  };

  const getPersistedQuickSalePos = (companyId, branchCode) => {
    try {
      return localStorage.getItem(getQuickSalePosStorageKey(companyId, branchCode)) || '';
    } catch {
      return '';
    }
  };

  // Cargar datos del emisor al iniciar - usando settingsService (igual que DTEWizard)
  useEffect(() => {
    const loadEmisorData = async () => {
      try {
        // Primero cargar desde localStorage como fallback
        const emisor = getEmisorData();
        
        // Cargar datos completos desde el backend usando settingsService
        const companyId = getCurrentCompanyId();
        if (companyId) {
          const response = await settingsService.getCompany(companyId);
          if (response.success && response.data) {
            console.log('[QUICKSALE] Datos completos del emisor cargados desde settingsService:', response.data);
            setEmisorData(response.data);
          } else if (emisor) {
            // Fallback a datos de localStorage
            console.log('[QUICKSALE] Usando datos del emisor desde localStorage:', emisor);
            setEmisorData(emisor);
          }
        } else if (emisor) {
          console.log('[QUICKSALE] Usando datos del emisor desde localStorage:', emisor);
          setEmisorData(emisor);
        }
      } catch (error) {
        console.error('Error cargando datos del emisor:', error);
        // Intentar con localStorage como fallback
        const emisor = getEmisorData();
        if (emisor) {
          setEmisorData(emisor);
        }
      }
    };
    loadEmisorData();
  }, []);
  
  // Cargar sucursales desde el backend
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const companyId = getCurrentCompanyId();
	        if (companyId) {
	          const branchesRes = await settingsService.getBranches(companyId);
	          if (branchesRes.success) {
	            const branchesData = branchesRes.data || [];
	            console.log('[QUICKSALE] Sucursales cargadas:', branchesData);
	            setBranches(branchesData);

	            const persisted = getPersistedQuickSaleBranch(companyId);
	            const persistedBranch = persisted
	              ? branchesData.find((branch) => branch.codigo_establecimiento === persisted)
	              : null;
	            const primaryBranch = branchesData.find((branch) => branch.is_primary === 1) || null;
	            const defaultBranch = persistedBranch || primaryBranch || branchesData[0] || null;

	            if (defaultBranch?.codigo_establecimiento) {
	              setDocument((prev) => ({
	                ...prev,
	                header: {
	                  ...prev.header,
	                  sucursal: defaultBranch.codigo_establecimiento,
                    branchId: defaultBranch.id,
	                },
	              }));
	              persistQuickSaleBranch(companyId, defaultBranch.codigo_establecimiento);
	            }
	          }
	        }
      } catch (error) {
        console.error('Error cargando sucursales:', error);
      }
    };
    loadBranches();
  }, []);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        const companyId = getCurrentCompanyId();
        if (companyId) {
          const productsRes = await authenticatedFetch(`https://nexai-billing-api.lmendoza-sv.workers.dev/api/products?companyId=${companyId}&limit=100`);
          if (productsRes.ok) {
            const productsData = await productsRes.json();
            setProducts(productsData);
            const uniqueCategories = [...new Set(productsData.map(p => p.category).filter(Boolean))];
            setCategories(uniqueCategories);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const loadPOS = async () => {
      try {
        const companyId = getCurrentCompanyId();
        if (!companyId) return;

        const posRes = await settingsService.getPOS(companyId);
        if (posRes.success) {
          setPointsOfSale(posRes.data || []);
        }
      } catch (error) {
        console.error('Error cargando puntos de venta:', error);
      }
    };

    loadPOS();
  }, []);
  
  const getCurrentCompanyId = () => {
    try {
      const modulesStr = localStorage.getItem('nx2026_modules');
      if (!modulesStr) return null;
      
      const modules = JSON.parse(modulesStr);
      if (modules && modules.length > 0 && modules[0].company) {
        return modules[0].company.id;
      }
      return null;
    } catch (error) {
      console.error('Error obtaining companyId:', error);
      return null;
    }
  };
  
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchSearch = !searchProduct || 
        p.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
        p.code.toLowerCase().includes(searchProduct.toLowerCase()) ||
        (p.barcode && p.barcode.includes(searchProduct));
      const matchCategory = !selectedCategory || p.category === selectedCategory;
      return matchSearch && matchCategory && p.is_active;
    });
  }, [products, searchProduct, selectedCategory]);

  const selectedBranch = resolveSelectedBranch(branches, document.header);
  const availablePointsOfSale = useMemo(() => (
    pointsOfSale.filter((pos) =>
      pos.branch_id === selectedBranch?.id && pos.deleted_at == null && pos.is_active !== 0
    )
  ), [pointsOfSale, selectedBranch?.id]);
  const selectedPos = useMemo(() => (
    availablePointsOfSale.find((pos) => pos.id === document.header?.posId) || null
  ), [availablePointsOfSale, document.header?.posId]);

  useEffect(() => {
    if (!selectedBranch) return;

    const companyId = getCurrentCompanyId();
    const branchCode = selectedBranch.codigo_establecimiento;
    const currentPos = availablePointsOfSale.find((pos) => pos.id === document.header?.posId) || null;
    const persistedPosId = getPersistedQuickSalePos(companyId, branchCode);
    const persistedPos = availablePointsOfSale.find((pos) => pos.id === persistedPosId) || null;
    const nextPos = currentPos || persistedPos || (availablePointsOfSale.length === 1 ? availablePointsOfSale[0] : null);
    const nextPosId = nextPos?.id || null;
    const nextPuntoVenta = nextPos?.codigo_punto_venta || '';

    setDocument((prev) => {
      if (prev.header?.posId === nextPosId && normalizePosValue(prev.header?.puntoVenta) === normalizePosValue(nextPuntoVenta)) {
        return prev;
      }

      return {
        ...prev,
        header: {
          ...prev.header,
          posId: nextPosId,
          puntoVenta: nextPuntoVenta,
        },
      };
    });

    if (nextPosId) {
      persistQuickSalePos(companyId, branchCode, nextPosId);
    }
  }, [availablePointsOfSale, document.header?.posId, selectedBranch]);
  
  const addToCart = (product) => {
    const existingIndex = cart.findIndex(item => item.productId === product.id);
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, {
        productId: product.id,
        code: product.code,
        descripcion: product.name,
        cantidad: 1,
        precioConIva: parseFloat(product.price),
        // precioUni = precio CON IVA (precio de venta al público)
        precioUni: parseFloat(product.price),
        tipoItem: product.dte_item_type || 1,
        tipoVenta: product.dte_sale_type || 'GRAVADA',
        // uniMedida debe ser siempre INT, no string
        uniMedida: parseInt(product.unit_measure, 10) || 13,
        montoDescu: 0,
        // Cálculo correcto según regla MH
        // ventaGravada = bruto (precio completo, IVA incluido)
        ventaGravada: parseFloat(product.price),
        // ivaItem = floor(bruto * 13 / 113 * 100) / 100
        ivaItem: Math.floor(parseFloat(product.price) * 13 / 113 * 100) / 100,
      }]);
    }
  };
  
  const updateCartQuantity = (index, newQuantity) => {
    if (newQuantity <= 0) {
      setCart(cart.filter((_, i) => i !== index));
    } else {
      const newCart = [...cart];
      newCart[index].cantidad = newQuantity;
      const item = newCart[index];
      const precioConIva = parseFloat(item.precioConIva);
      // precioUni = precio CON IVA (precio de venta al público)
      item.precioUni = precioConIva;
      // Cálculo correcto según regla MH
      const bruto = parseFloat((precioConIva * newQuantity).toFixed(2));
      item.ventaGravada = bruto;  // precio completo, IVA incluido
      item.ivaItem = Math.floor(bruto * 13 / 113 * 100) / 100;
      setCart(newCart);
    }
  };
  
  const cartTotals = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => sum + (item.precioConIva * item.cantidad), 0);
    // Para CCF el IVA ya está incluido en precioConIva, calcularlo correctamente
    const baseGravada = subtotal / 1.13;
    const iva = subtotal - baseGravada;
    const total = subtotal;
    return { subtotal, iva, total };
  }, [cart]);
  
  const handleTipoDteChange = (tipoDte) => {
    setDocument(prev => ({
      ...prev,
      identificacion: { ...prev.identificacion, tipoDte },
      receptor: null,
    }));
    setActiveStep(1);
  };
  
  const handleReceptorSelect = (receptor) => {
    if (receptor) {
      let telefonoValue = receptor.telefono || '';
      // Normalizar teléfono para cumplir schema (solo dígitos, sin prefijo país)
      const digitsOnly = String(telefonoValue).replace(/\D/g, '');
      const countryPrefixes = COUNTRIES.map(c => c.prefix);
      const regexPrefix = new RegExp(`^(${countryPrefixes.join('|')})+`, '');
      telefonoValue = digitsOnly.replace(regexPrefix, '');
      // Si queda más largo (por ejemplo incluye código + número), usar los últimos 8 dígitos
      if (telefonoValue.length > 8) {
        telefonoValue = telefonoValue.slice(-8);
      }
      
      setDocument(prev => ({
        ...prev,
        receptor: {
          ...prev.receptor,
          id: receptor.id || null,
          tipoDocumento: receptor.tipoDocumento || '',
          numDocumento: receptor.numDocumento || '',
          nombre: receptor.nombre || '',
          nombreComercial: receptor.nombreComercial || '',
          nrc: receptor.nrc || '',
          correo: receptor.correo || '',
          correoNotificacion: receptor.correo || receptor.correoNotificacion || '',
          telefono: telefonoValue,
          whatsapp: telefonoValue,
          direccion: receptor.direccion
            ? {
                ...receptor.direccion,
                // A veces viene como "0601" (CAT-012 full code); el DTE espera solo 2 dígitos.
                departamento: receptor.direccion?.departamento
                  ? String(receptor.direccion.departamento).slice(-2).padStart(2, '0')
                  : receptor.direccion?.departamento,
                municipio: receptor.direccion?.municipio
                  ? String(receptor.direccion.municipio).slice(-2).padStart(2, '0')
                  : receptor.direccion?.municipio,
              }
            : null,
          codActividad: receptor.codActividad || '',
          descActividad: receptor.descActividad || '',
        },
      }));
    }
  };
  
  const handleClienteModeChange = (mode) => {
    setClienteMode(mode);
    if (mode === 'nuevo') {
      setDocument(prev => ({
        ...prev,
        receptor: {
          tipoDocumento: '',
          numDocumento: '',
          nombre: '',
          nombreComercial: '',
          nrc: '',
          correo: '',
          correoNotificacion: '',
          telefono: '',
          direccion: null,
          codActividad: '',
          descActividad: '',
        },
      }));
    }
  };
  
  const validateStep = (step) => {
    const newErrors = {};
    const tipoDte = document.identificacion.tipoDte;
    
    switch (step) {
      case 0:
        if (!document.identificacion.tipoDte) {
          newErrors['identificacion.tipoDte'] = 'Seleccione un tipo de documento';
        }
        break;
      case 1:
        if (!document.receptor) {
          newErrors['receptor'] = 'Datos del receptor son requeridos';
        } else if (!document.receptor.nombre) {
          newErrors['receptor.nombre'] = 'Nombre o razón social es requerido';
        } else if (tipoDte === '03') {
          const nrc = String(document.receptor.nrc || '').trim();
          const codActividad = String(document.receptor.codActividad || '').trim();
          const descActividad = String(document.receptor.descActividad || '').trim();
          const direccion = document.receptor.direccion;

          if (!nrc) {
            newErrors['receptor.nrc'] = 'NRC es requerido para Crédito Fiscal (CCF)';
          }

          if (!codActividad) {
            newErrors['receptor.codActividad'] = 'Código de actividad económica es requerido para CCF';
          } else if (!/^[0-9]{5,6}$/.test(codActividad)) {
            newErrors['receptor.codActividad'] = 'Código de actividad económica debe tener 5 a 6 dígitos';
          }

          if (!descActividad) {
            newErrors['receptor.descActividad'] = 'Descripción de actividad económica es requerida para CCF';
          } else if (descActividad.length < 5) {
            newErrors['receptor.descActividad'] = 'Descripción de actividad económica debe tener al menos 5 caracteres';
          }

          if (!direccion || typeof direccion !== 'object') {
            newErrors['receptor.direccion'] = 'Dirección del receptor es requerida para CCF';
          } else {
            const departamento = String(direccion.departamento || '').trim();
            // Puede venir como "0601" desde catálogos/BD; el DTE solo usa los últimos 2 dígitos.
            const municipioRaw = String(direccion.municipio || '').trim();
            const municipio = municipioRaw ? municipioRaw.slice(-2) : '';
            const complemento = String(direccion.complemento || '').trim();

            if (!/^[0-9]{2}$/.test(departamento)) {
              newErrors['receptor.direccion'] = 'Dirección inválida: departamento debe ser de 2 dígitos';
            } else if (!/^[0-9]{2}$/.test(municipio)) {
              newErrors['receptor.direccion'] = 'Dirección inválida: municipio debe ser de 2 dígitos';
            } else if (!complemento || complemento.length < 5) {
              newErrors['receptor.direccion'] = 'Dirección inválida: complemento debe tener al menos 5 caracteres';
            }
          }
        }
        break;
      case 2:
        if (!branches.length || !selectedBranch) {
          newErrors['header.sucursal'] = 'Seleccione una sucursal';
        }
        if (!availablePointsOfSale.length) {
          newErrors['header.posId'] = 'No hay cajas configuradas para esta sucursal';
        } else if (!selectedPos) {
          newErrors['header.posId'] = 'Seleccione una caja';
        }
        if (cart.length === 0) {
          newErrors['cart'] = 'Agregue al menos un producto';
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleNext = () => {
    // Logging para cada paso
    if (activeStep === 0) {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('PASO 1: TIPO DE DOCUMENTO SELECCIONADO');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Tipo DTE:', document.identificacion.tipoDte);
      console.log('Nombre:', QUICK_SALE_DTE_TYPES.find(d => d.code === document.identificacion.tipoDte)?.name);
    }
    
    // Mostrar datos del emisor cuando avanza al paso de productos
    if (activeStep === 1) {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('PASO 2: DATOS DEL CLIENTE');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Tipo DTE:', document.identificacion.tipoDte);
      console.log('Receptor:', JSON.stringify(document.receptor, null, 2));
    }
    
    // Mostrar datos del cuerpo del documento
    if (activeStep === 2) {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('PASO 3: DETALLE DEL DOCUMENTO');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Tipo DTE:', document.identificacion.tipoDte);
      console.log('Fecha Emisión:', document.identificacion?.fecEmi);
      console.log('Cuerpo Documento:', JSON.stringify(cart, null, 2));
    }
    
    const isValid = validateStep(activeStep);
    if (!isValid) return;
    
    setActiveStep(prev => prev + 1);
  };
  
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };
  
  const handleSignAndSend = async () => {
    setIsSubmitting(true);
    setSubmitResult(null);
    setSchemaDiagnostics([]);
    
    let signResult = null;
    
    try {
      // Defensa extra: si por alguna razón se intenta enviar sin pasar por el stepper,
      // volver a validar el step 1 (receptor) para CCF y bloquear si falta data.
      if (!validateStep(1)) {
        setActiveStep(1);
        return;
      }

      const docToSend = {
        ...document,
        cuerpoDocumento: cart.map((item, index) => ({
          ...item,
          numItem: index + 1,
        })),
      };
      
      const jsonDTE = stripEmptyOptionalFields(buildCompleteDTEJson(docToSend, emisorData, branches));

      const schemaValidation = validateDtePayload(jsonDTE);
      setSchemaDiagnostics(schemaValidation.diagnostics || []);
      if (schemaValidation.diagnostics?.length) {
        console.warn('[QUICK SALE] Diagnóstico AJV MH:', schemaValidation.diagnostics);
      }
      
      console.log('=== QUICK SALE: Firmando y enviando a MH ===');
      console.log('Tipo DTE:', document.identificacion.tipoDte);
      console.log('Codigo Generacion:', jsonDTE.identificacion.codigoGeneracion);

      // Validar contra esquema (server-side) antes de crear factura / firmar / enviar
      const validationResponse = await authenticatedFetch('https://nexai-billing-api.lmendoza-sv.workers.dev/api/dte/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dteJson: jsonDTE }),
      });

      const validationResult = await parseJsonResponse(validationResponse, 'No se pudo validar el DTE');
      if (!validationResponse.ok || validationResult?.success === false) {
        setSubmitResult({
          success: false,
          message: validationResult?.message || 'Validación fallida',
          observaciones: validationResult?.observaciones || null,
        });
        return;
      }

      const companyId = getCurrentCompanyId();
      
      const isCCF = document.identificacion.tipoDte === '03';
      const totalGravadaCalc = round(cart.reduce((sum, item) => {
        const cantidad = parseFloat(item.cantidad) || 1;
        const precioConIva = parseFloat(item.precioConIva ?? item.precioUni) || 0;
        const descuentoConIva = parseFloat(item.montoDescu) || 0;
        if (isCCF) {
          return sum + toBaseAmount((precioConIva * cantidad) - (descuentoConIva * cantidad));
        }
        const precioBase = parseFloat(item.precioUni) || 0;
        return sum + ((precioBase * cantidad) - (descuentoConIva * cantidad));
      }, 0), 2);
      const totalIVACalc = isCCF ? toIvaAmount(totalGravadaCalc) : round(totalGravadaCalc * IVA_RATE, 2);
      const montoFinal = isNaN(totalGravadaCalc) ? 0 : totalGravadaCalc;
      const ivaFinal = isNaN(totalIVACalc) ? 0 : totalIVACalc;
      
      const tipoDteToTipoFactura = {
        '01': 'CF',
        '03': 'CCF',
        '14': 'EXC',
      };
      
      const response = await authenticatedFetch('https://nexai-billing-api.lmendoza-sv.workers.dev/api/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          branch_id: selectedBranch?.id || null,
          pos_id: selectedPos?.id || null,
          tipo: tipoDteToTipoFactura[document.identificacion.tipoDte] || 'CF',
          monto: montoFinal,
          iva: ivaFinal,
          retencion: 0,
          detalles: cart.map((item) => {
            const precioConIva = parseFloat(item.precioConIva ?? item.precioUni) || 0;
            const descuentoConIva = parseFloat(item.montoDescu) || 0;
            const cantidad = parseFloat(item.cantidad) || 1;
            const totalBaseCcf = isCCF ? toBaseAmount((precioConIva * cantidad) - (descuentoConIva * cantidad)) : 0;
            const precioDetalle = isCCF
              ? (cantidad > 0 ? round(totalBaseCcf / cantidad, 3) : toBaseUnitAmount(precioConIva))
              : (parseFloat(item.precioUni) || 0);
            const totalDetalle = isCCF
              ? totalBaseCcf
              : round((precioConIva * (parseFloat(item.cantidad) || 1)) - (descuentoConIva * (parseFloat(item.cantidad) || 1)), 2);

            return {
            descripcion: item.descripcion,
            cantidad: item.cantidad,
            precio: precioDetalle,
            total: totalDetalle,
            tipoVenta: item.tipoVenta || 'GRAVADA',
            uniMedida: item.uniMedida || 13,
            descuento: 0,
            };
          }),
          json_data: jsonDTE
        })
      });
      
      if (!response.ok) {
        const error = await parseJsonResponse(response, 'No se pudo crear la factura');
        throw new Error(error.error || 'Error al crear la factura');
      }
      
      const facturaCreada = await parseJsonResponse(response, 'No se pudo leer la factura creada');
      const documentId = facturaCreada.id;
      const persistedDteJson = parseInvoiceJsonData(facturaCreada?.json_data);
      const finalDteJson = stripEmptyOptionalFields(mergeBackendDteIdentity(jsonDTE, persistedDteJson));
      
	      const signResponse = await authenticatedFetch('https://nexai-billing-api.lmendoza-sv.workers.dev/api/dte/sign-and-send', {
	        method: 'POST',
	        headers: { 'Content-Type': 'application/json' },
	        body: JSON.stringify({
	          documentId,
	          companyId: companyId,
	          source: 'quickdte',
	          dteJson: finalDteJson
	        })
	      });
      
      signResult = await parseJsonResponse(signResponse, 'No se pudo firmar y enviar el DTE');
      
      if (signResponse.ok && signResult.success) {
        setSubmitResult({
          success: true,
          message: 'Documento firmado y enviado a MH exitosamente',
          codigoGeneracion: finalDteJson.identificacion?.codigoGeneracion || jsonDTE.identificacion.codigoGeneracion,
          selloRecepcion: signResult.selloRecepcion,
          status: signResult.status
        });
        
        setCart([]);
      } else {
        throw new Error(signResult.message || signResult.error || 'Error al firmar/enviar a MH');
      }
      
    } catch (error) {
      console.error('[QUICK SALE] Error:', error);
      
      // Incluir las observaciones del MH cuando hay error de esquema
      setSubmitResult({
        success: false,
        message: error.message,
        observaciones: signResult?.observaciones || null,
        clasificaMsg: signResult?.clasificaMsg || null,
        codigoMsg: signResult?.codigoMsg || null,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderStep1DocumentType();
      case 1:
        return renderStep2Client();
      case 2:
        return renderStep3POS();
      case 3:
        return renderStep4Summary();
      default:
        return null;
    }
  };
  
  const renderStep1DocumentType = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Seleccione el tipo de documento
      </Typography>
      <Grid container spacing={2}>
        {QUICK_SALE_DTE_TYPES.map((dte) => (
          <Grid item xs={12} sm={4} key={dte.code}>
            <Card
              sx={{
                cursor: 'pointer',
                border: document.identificacion.tipoDte === dte.code 
                  ? `3px solid ${dte.color}` 
                  : '2px solid #e0e0e0',
                transition: 'all 0.2s',
                '&:hover': { 
                  transform: 'scale(1.02)',
                  boxShadow: 4,
                  borderColor: dte.color,
                },
                minHeight: 150,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={() => handleTipoDteChange(dte.code)}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <ReceiptLong sx={{ fontSize: 48, color: dte.color, mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  {dte.shortName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dte.name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {errors['identificacion.tipoDte'] && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors['identificacion.tipoDte']}
        </Alert>
        )}

        {schemaDiagnostics.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Diagnóstico AJV de MH: {schemaDiagnostics.length} observación(es)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Estas observaciones se registran para la migración y no bloquean el envío en esta fase.
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {schemaDiagnostics.map((diagnostic, index) => (
                <li key={`${diagnostic.path}-${diagnostic.keyword}-${index}`}>
                  <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    [{diagnostic.section}] {diagnostic.path}: {diagnostic.message}
                  </Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}
    </Box>
  );
  
  const renderStep2Client = () => {
    const isCCF = document.identificacion.tipoDte === '03';
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Datos del Cliente
        </Typography>
        
        <ToggleButtonGroup
          value={clienteMode}
          exclusive
          onChange={(e, value) => value && handleClienteModeChange(value)}
          sx={{ mb: 3, width: '100%' }}
          fullWidth
        >
          <ToggleButton value="existente">
            <Person sx={{ mr: 1 }} /> Cliente Existente
          </ToggleButton>
          <ToggleButton value="nuevo">
            <AddIcon sx={{ mr: 1 }} /> Cliente Nuevo
          </ToggleButton>
        </ToggleButtonGroup>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Documento</InputLabel>
              <Select
                value={document.receptor?.tipoDocumento || ''}
                label="Tipo de Documento"
                onChange={(e) =>
                  setDocument(prev => ({
                    ...prev,
                    receptor: { 
                      ...prev.receptor, 
                      tipoDocumento: e.target.value, 
                      numDocumento: '',
                      nombre: '' 
                    },
                  }))
                }
              >
                <MenuItem value="36">36 - NIT</MenuItem>
                <MenuItem value="13">13 - DUI</MenuItem>
                <MenuItem value="02">02 - Carnet Residente</MenuItem>
                <MenuItem value="03">03 - Pasaporte</MenuItem>
                <MenuItem value="37">37 - Otro</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {document.receptor?.tipoDocumento && (
            <Grid item xs={12} sm={6}>
              <ReceptorSearch
                tipoDocumento={document.receptor?.tipoDocumento || ''}
                numDocumento={document.receptor?.numDocumento || ''}
                receptor={document.receptor}
                onSelect={handleReceptorSelect}
                companyId={getCurrentCompanyId()}
                onInputChange={(value) => {
                  setDocument(prev => ({
                    ...prev,
                    receptor: { ...prev.receptor, numDocumento: value },
                  }));
                }}
                label="Buscar / Ingresar Número"
                required={true}
                error={errors['receptor.numDocumento'] || ''}
                disabled={false}
              />
            </Grid>
          )}
          
          {document.receptor?.numDocumento && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre / Razón Social"
                value={document.receptor?.nombre || ''}
                onChange={(e) =>
                  setDocument(prev => ({
                    ...prev,
                    receptor: { ...prev.receptor, nombre: e.target.value },
                  }))
                }
                error={!!errors['receptor.nombre']}
                helperText={errors['receptor.nombre']}
                required
              />
            </Grid>
          )}
          
          {isCCF && document.receptor?.nombre && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="NRC"
                  value={document.receptor?.nrc || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setDocument(prev => ({
                      ...prev,
                      receptor: { ...prev.receptor, nrc: value },
                    }));
                  }}
                  inputProps={{ maxLength: 8 }}
                  error={!!errors['receptor.nrc']}
                  helperText={errors['receptor.nrc']}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  value={document.receptor?.correoNotificacion || ''}
                  onChange={(e) =>
                    setDocument(prev => ({
                      ...prev,
                      receptor: { ...prev.receptor, correoNotificacion: e.target.value },
                    }))
                  }
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={document.receptor?.telefono || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                    setDocument(prev => ({
                      ...prev,
                      receptor: { ...prev.receptor, telefono: value, whatsapp: value },
                    }));
                  }}
                  inputProps={{ maxLength: 8 }}
                />
              </Grid>
            </>
          )}
          
          {(document.identificacion.tipoDte === '01' || document.identificacion.tipoDte === '14') && document.receptor?.nombre && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Contacto para recibir el DTE (Opcional)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Correo para recibir DTE"
                    value={document.receptor?.correoNotificacion || ''}
                    onChange={(e) =>
                      setDocument(prev => ({
                        ...prev,
                        receptor: { ...prev.receptor, correoNotificacion: e.target.value },
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl sx={{ minWidth: 85 }}>
                      <InputLabel>País</InputLabel>
                      <Select
                        value={whatsappCountry}
                        label="País"
                        onChange={(e) => setWhatsappCountry(e.target.value)}
                      >
                        {COUNTRIES.map((country) => (
                          <MenuItem key={country.code} value={country.prefix}>
                            +{country.prefix}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="WhatsApp"
                      value={document.receptor?.whatsapp || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                        setDocument(prev => ({
                          ...prev,
                          receptor: { ...prev.receptor, whatsapp: value },
                        }));
                      }}
                      inputProps={{ maxLength: 8 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
        
        {errors['receptor'] && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors['receptor']}
          </Alert>
        )}

        {(errors['receptor.codActividad'] || errors['receptor.descActividad'] || errors['receptor.direccion']) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors['receptor.codActividad'] && <div>{errors['receptor.codActividad']}</div>}
            {errors['receptor.descActividad'] && <div>{errors['receptor.descActividad']}</div>}
            {errors['receptor.direccion'] && <div>{errors['receptor.direccion']}</div>}
          </Alert>
        )}
      </Box>
    );
  };
  
  const renderStep3POS = () => {
    // Obtener la fecha máxima permitida (hoy) para el date picker
    const today = new Date().toISOString().split('T')[0];
    
	    return (
	      <Box sx={{ mt: 2 }}>
	        <Box sx={{ mb: 2 }}>
	          <Grid container spacing={2} alignItems="center">
	            <Grid item xs={12} sm={6} md={3}>
	              <FormControl fullWidth error={!!errors['header.sucursal']} disabled={!branches.length}>
	                <InputLabel>Sucursal</InputLabel>
	                <Select
	                  value={document.header?.sucursal || ''}
	                  label="Sucursal"
	                  onChange={(e) => {
	                    const companyId = getCurrentCompanyId();
	                    const value = e.target.value;
                      const branch = branches.find((item) =>
                        normalizeBranchValue(item.codigo_establecimiento) === normalizeBranchValue(value)
                      );
	                    setDocument((prev) => ({
	                      ...prev,
	                      header: {
                          ...prev.header,
                          sucursal: value,
                          branchId: branch?.id || null,
                        },
	                    }));
	                    persistQuickSaleBranch(companyId, value);
	                  }}
	                >
	                  {branches.map((branch) => (
	                    <MenuItem key={branch.id} value={branch.codigo_establecimiento}>
	                      {branch.branch_name} ({branch.codigo_establecimiento})
	                    </MenuItem>
	                  ))}
	                </Select>
	                {errors['header.sucursal'] && <FormHelperText>{errors['header.sucursal']}</FormHelperText>}
	              </FormControl>
	            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors['header.posId']} disabled={!selectedBranch || !availablePointsOfSale.length}>
                <InputLabel>Caja</InputLabel>
                <Select
                  value={document.header?.posId || ''}
                  label="Caja"
                  onChange={(e) => {
                    const companyId = getCurrentCompanyId();
                    const posId = e.target.value;
                    const pos = availablePointsOfSale.find((item) => item.id === posId) || null;

                    setDocument((prev) => ({
                      ...prev,
                      header: {
                        ...prev.header,
                        posId,
                        puntoVenta: pos?.codigo_punto_venta || '',
                      },
                    }));

                    if (selectedBranch?.codigo_establecimiento && posId) {
                      persistQuickSalePos(companyId, selectedBranch.codigo_establecimiento, posId);
                    }
                  }}
                >
                  {availablePointsOfSale.map((pos) => (
                    <MenuItem key={pos.id} value={pos.id}>
                      {pos.pos_name} ({normalizePosValue(pos.codigo_punto_venta)})
                    </MenuItem>
                  ))}
                </Select>
                {errors['header.posId'] && <FormHelperText>{errors['header.posId']}</FormHelperText>}
              </FormControl>
            </Grid>
	            {/* Selector de Fecha de Emisión */}
	            <Grid item xs={12} sm={6} md={3}>
	              <TextField
	                fullWidth
                label="Fecha de Emisión"
                type="date"
                value={fechaEmision}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  setFechaEmision(selectedDate);
                  setDocument(prev => ({
                    ...prev,
                    identificacion: { ...prev.identificacion, fecEmi: selectedDate }
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: today  // No permite fechas futuras
                }}
                // helperText="No se permiten fechas futuras"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                placeholder="Buscar productos..."
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Categoría"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        
        {errors['cart'] && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors['cart']}
          </Alert>
        )}
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Productos ({filteredProducts.length})
            </Typography>
            <Box sx={{ 
              maxHeight: 500, 
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
            }}>
              {filteredProducts.map((product) => (
                <Card
                  key={product.id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      transform: 'scale(1.02)',
                      boxShadow: 3,
                    },
                    p: 1,
                    textAlign: 'center',
                  }}
                  onClick={() => addToCart(product)}
                >
                  <Typography variant="body2" fontWeight="bold" noWrap>
                    {product.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {product.code}
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${parseFloat(product.price).toFixed(2)}
                  </Typography>
                  <Chip 
                    label={product.category || 'Sin categoría'} 
                    size="small" 
                    sx={{ mt: 0.5, fontSize: '0.7rem' }}
                  />
                </Card>
              ))}
            </Box>
            
            {filteredProducts.length === 0 && (
              <Alert severity="info">
                No hay productos disponibles.
              </Alert>
            )}
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Card sx={{ bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    <ShoppingCart sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Carrito ({cart.length})
                  </Typography>
                  {cart.length > 0 && (
                    <Button 
                      size="small" 
                      color="error"
                      onClick={() => setCart([])}
                    >
                      Limpiar
                    </Button>
                  )}
                </Box>
                
                <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                  {cart.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                      Seleccione productos para agregar
                    </Typography>
                  ) : (
                    cart.map((item, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          p: 1,
                          mb: 1,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>
                            {item.descripcion}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ${item.precioConIva.toFixed(2)} c/u
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton 
                            size="small" 
                            onClick={() => updateCartQuantity(index, item.cantidad - 1)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="body1" sx={{ minWidth: 30, textAlign: 'center' }}>
                            {item.cantidad}
                          </Typography>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => updateCartQuantity(index, item.cantidad + 1)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 60, textAlign: 'right' }}>
                          ${(item.precioConIva * item.cantidad).toFixed(2)}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
                
                {cart.length > 0 && (
                  <Divider sx={{ my: 2 }} />
                )}
                
                {cart.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Subtotal:</Typography>
                      <Typography variant="body2">${cartTotals.subtotal.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="error">IVA (13%):</Typography>
                      <Typography variant="body2" color="error">${cartTotals.iva.toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" fontWeight="bold">Total:</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary">
                        ${cartTotals.total.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  const renderStep4Summary = () => {
    const currentDTE = QUICK_SALE_DTE_TYPES.find(d => d.code === document.identificacion.tipoDte);
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Resumen del Documento
        </Typography>
        
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Tipo</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {currentDTE?.name}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {document.receptor?.nombre || 'CONSUMIDOR FINAL'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Documento</Typography>
                <Typography variant="body1">
                  {document.receptor?.numDocumento || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Items</Typography>
                <Typography variant="body1">
                  {cart.length} producto{cart.length !== 1 ? 's' : ''}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Detalle de Totales
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {cart.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2">
                      {item.cantidad}x {item.descripcion}
                    </Typography>
                    <Typography variant="body2">
                      ${(item.precioConIva * item.cantidad).toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="body1">Subtotal:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body1">${cartTotals.subtotal.toFixed(2)}</Typography>
              </Grid>
              
              {document.identificacion.tipoDte !== '14' && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body1" color="error">IVA (13%):</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" color="error">${cartTotals.iva.toFixed(2)}</Typography>
                  </Grid>
                </>
              )}
              
              {document.identificacion.tipoDte === '14' && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body1">RENTA 10%:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1">-${(cartTotals.subtotal * 0.10).toFixed(2)}</Typography>
                  </Grid>
                </>
              )}
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="h5" fontWeight="bold">TOTAL:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  ${cartTotals.total.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        {submitResult && (
          <Alert 
            severity={submitResult.success ? 'success' : 'error'} 
            sx={{ mb: 2 }}
          >
            {submitResult.message}
            {submitResult.codigoGeneracion && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Código:</strong> {submitResult.codigoGeneracion}
              </Typography>
            )}
            {/* Mostrar observaciones del MH cuando hay errores de esquema */}
            {submitResult.observaciones && submitResult.observaciones.length > 0 && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="error" gutterBottom>
                  Errores de Validación detectados por MH:
                </Typography>
                {submitResult.clasificaMsg && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Código de clasificación:</strong> {submitResult.clasificaMsg}
                    {submitResult.codigoMsg && <span> - Código: {submitResult.codigoMsg}</span>}
                  </Typography>
                )}
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {submitResult.observaciones.map((obs, index) => (
                    <li key={index}>
                      <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {obs}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </Alert>
        )}
        
        {!submitResult?.success && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
              onClick={handleSignAndSend}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enviando a MH...' : 'Firmar y Enviar a MH'}
            </Button>
          </Box>
        )}
        
        {submitResult?.success && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color="success"
	              size="large"
	              startIcon={<AddIcon />}
	              onClick={() => {
	                const companyId = getCurrentCompanyId();
	                const persistedBranch = getPersistedQuickSaleBranch(companyId);
	                const nextSucursal = persistedBranch || document.header?.sucursal || '0';
                  const nextBranch = branches.find((branch) =>
                    normalizeBranchValue(branch.codigo_establecimiento) === normalizeBranchValue(nextSucursal)
                  );
                  const persistedPosId = nextBranch?.codigo_establecimiento
                    ? getPersistedQuickSalePos(companyId, nextBranch.codigo_establecimiento)
                    : '';
                  const nextPos = pointsOfSale.find((pos) =>
                    pos.branch_id === nextBranch?.id && pos.id === persistedPosId && pos.deleted_at == null && pos.is_active !== 0
                  ) || null;
	                setActiveStep(0);
	                setDocument({
	                  identificacion: { tipoDte: '', fecEmi: new Date().toISOString().split('T')[0] },
	                  header: {
                      formaPago: 'contado',
                      metodoPago: '01',
                      sucursal: nextSucursal,
                      branchId: nextBranch?.id || null,
                      puntoVenta: nextPos?.codigo_punto_venta || '',
                      posId: nextPos?.id || null,
                    },
	                  receptor: null,
	                  cuerpoDocumento: [],
	                });
	                setFechaEmision(new Date().toISOString().split('T')[0]);
	                setCart([]);
                setSubmitResult(null);
              }}
            >
              Nueva Venta
            </Button>
          </Box>
        )}
      </Box>
    );
  };
  
  const steps = [
    { label: 'Tipo', icon: <ReceiptLong /> },
    { label: 'Cliente', icon: <Person /> },
    { label: 'Productos', icon: <ShoppingCart /> },
    { label: 'Enviar', icon: <Send /> },
  ];
  
  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        <PointOfSale sx={{ mr: 1, verticalAlign: 'middle' }} />
        Venta Rápida
      </Typography>
      
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                    color: index <= activeStep ? 'white' : 'grey.500',
                  }}
                >
                  {step.icon}
                </Box>
              )}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      
      <Card>
        <CardContent sx={{ p: 3 }}>
          {renderStepContent()}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              Atrás
            </Button>
            
            {activeStep < steps.length - 1 && (
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleNext}
              >
                Siguiente
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default QuickSaleWizard;
